import React, {useEffect, useRef, useState} from "react";
import { Ratio } from "react-bootstrap";
import Cropper, {Area} from "react-easy-crop";
import getCroppedImg from "../../features/ImageCrop/cropImage";
import ImageZoomSlider from "./ImageZoomSlider";
import toast from "react-hot-toast";


type Props = {
    image?: string;
    cropShape?: 'rect' | 'round' | undefined;
    aspect?: number | undefined;
    onCropApplied?: (image: Blob, filename: string, type: string) => void
    onCancel?: () => void
};

const ImageCropper = ({image, cropShape, aspect, onCropApplied, onCancel}: Props) => {
    const inputFileRef = useRef<HTMLInputElement | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);
    const [type, setType] = useState<string | null>(null);
    const [filename, setFilename] = useState<string | null>(null);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    useEffect(() => {
        if (image) {
            setUploadedImage(image);
            setType('image/webp');
            setFilename(image.split('/').pop() ?? 'image.webp');
        }
    }, [image]);

    const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    }

    const handleApplyCropping = async () => {
        if (uploadedImage && croppedAreaPixels) {
            const croppedImage = await getCroppedImg(
                uploadedImage,
                croppedAreaPixels,
                0
            )

            if (onCropApplied && croppedImage && type && filename) {
                onCropApplied(croppedImage, filename, type);
            }
        }
    }

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];



        if (file) {
            if (file.size > 10485760) {
                toast.error('File must be under 10MB')
            } else {
                const reader = new FileReader();

                reader.onloadend = () => {
                    setUploadedImage(reader.result as string);
                    setType(file.type);
                    setFilename(file.name);
                };
                reader.readAsDataURL(file);
            }
        }
    };

    return (
        <div className='anim-delay-fast'>
            {uploadedImage && <>
                <>
                    <Ratio aspectRatio={(1 / (aspect ?? 1)) * 100}>
                        <Cropper
                            image={uploadedImage}
                            crop={crop}
                            zoom={zoom}
                            cropShape={cropShape}
                            aspect={aspect ?? 1}
                            zoomWithScroll={false}
                            showGrid={false}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            objectFit='cover'
                        />
                    </Ratio>
                </>

                <div className='slide-top'>
                    <ImageZoomSlider onChange={(value: number) => setZoom(value)} />
                </div>
            </>
            }
            <div className='row mt-3 slide-bottom'>
                <div className='col-auto'>
                    <button type='button' className='btn btn-sm btn-light' onClick={handleCancel}>Cancel</button>
                </div>
                <div className='col-auto'>
                    <button className='btn btn-sm btn-primary' type='button'
                            onClick={() => inputFileRef.current?.click()}>Select image
                    </button>
                    <input
                        type="file"
                        id="fileInput"
                        accept=".png, .jpg, .jpeg, .webp"
                        style={{display: 'none'}}
                        onChange={handleImageUpload}
                        ref={inputFileRef}
                    />
                </div>
                <div className='col-auto'>
                    <button type='button' className='btn btn-sm btn-primary' onClick={handleApplyCropping}>Save</button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropper;
